import { Injectable } from '@angular/core'
import {
  EventType,
  GlobalMessagingService,
  HeliosMessageType,
  JobCompleteEvent,
} from '@helios/messaging'
import {
  RootState,
  selectActiveSession,
  selectSessions,
  Session,
  setIsAffectedByJobRun,
} from '@helios/state-management'
import { UserPreferencesService } from '@helios/user-preferences'
import { compareSets } from '@helios/utils'
import { Store } from '@ngrx/store'
import { ToastService } from '@northfork/ng-basics'
import { distinctUntilChanged, map } from 'rxjs/operators'
import { SessionsService } from '../sessions-service/sessions.service'

@Injectable({
  providedIn: 'root',
})
export class JobCompletionService {
  /** @internal */
  _sessions: Session[] = []
  /** @internal */
  _activeSession?: Session

  autoRefresh = false
  constructor(
    private readonly store: Store<RootState>,
    private readonly toastService: ToastService,
    private readonly messagingService: GlobalMessagingService,
    private readonly userPreferencesService: UserPreferencesService,
    private readonly sessionService: SessionsService,
  ) {
    this.userPreferencesService
      .getAutoRefreshAccount()
      .subscribe((autoRefresh: boolean) => {
        this.autoRefresh = autoRefresh
      })
    this.initStore()
  }

  /** @internal */
  initStore() {
    this.store
      .select(selectSessions)
      .pipe(
        distinctUntilChanged((a, b) =>
          compareSets(new Set(a.map(x => x.id)), new Set(b.map(x => x.id))),
        ),
      )
      .subscribe(sessions => this.setSessions(sessions))

    this.store
      .select(selectActiveSession)
      .pipe(distinctUntilChanged((a, b) => a?.id === b?.id))
      .subscribe(activeSession => this.setActiveSession(activeSession))
  }

  /** @internal for testing */
  setActiveSession(activeSession: Session | undefined) {
    this._activeSession = activeSession
  }

  setSessions(sessions: Session[]) {
    this._sessions = sessions
  }

  init() {
    this.messagingService
      .on<unknown, JobCompleteEvent>(
        EventType.MESSAGE,
        HeliosMessageType.JobComplete,
      )
      .pipe(map(event => event.payload.affectedAccountIds))
      .subscribe(affectedAccountIds => {
        if (this._activeSession == null) return
        if (this.autoRefresh) {
          this.refreshSessionIfActiveSessionAffected(
            affectedAccountIds,
            this._activeSession,
          )
        } else {
          this.notifyUserIfActiveSessionAffected(
            affectedAccountIds,
            this._activeSession,
          )
          this.dispatchAffectedSessions(
            this.getAffectedSessions(affectedAccountIds),
          )
        }
      })
  }

  /** @internal */
  dispatchAffectedSessions(affectedSessions: string[]): void {
    if (affectedSessions.length === 0) {
      return
    }

    this.store.dispatch(
      setIsAffectedByJobRun({
        sessionIds: affectedSessions,
      }),
    )
  }

  /** @internal */
  getAffectedSessions(affectedAccountIds: number[]): string[] {
    const affectedSessions: string[] = []
    this._sessions.forEach(session => {
      // setIsAffectedByJobRun
      const isAffected = session.loadedAccountIds.some(id =>
        affectedAccountIds.includes(id),
      )
      if (isAffected) {
        affectedSessions.push(session.id)
      }
    })

    return affectedSessions
  }

  notifyUserIfActiveSessionAffected(
    affectedAccountIds: number[],
    activeSession: Session,
  ) {
    const affectedLoadedAccountIds = affectedAccountIds.filter(id =>
      activeSession.loadedAccountIds.includes(id),
    )
    if (affectedLoadedAccountIds.length === 0) return
    this.toastService
      .warning(`The following accounts were ran: ${affectedLoadedAccountIds.join(
      ', ',
    )}.
    Data in your session may be stale. Refresh is available on the session card.`)
  }
  refreshSessionIfActiveSessionAffected(
    affectedAccountIds: number[],
    activeSession: Session,
  ) {
    const affectedLoadedAccountIds = affectedAccountIds.filter(id =>
      activeSession.loadedAccountIds.includes(id),
    )
    if (affectedLoadedAccountIds.length === 0) return
    this.sessionService.autoRefreshSessionOnJobComplete(
      activeSession,
      affectedLoadedAccountIds,
    )
  }
}
